import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { userService, parentService, quizService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Badge, Button, EmptyState, FullPageSpinner, Modal } from '../../components/ui';

function ProgressBar({ value }) {
  return (
    <div className="w-full bg-gray-100 rounded-full h-2">
      <div className="bg-primary-600 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

// One tab, active/hover states in a single place — same pill-style pattern
// used on the module detail page, so a parent's child-progress page reads
// consistently with the rest of the app instead of one long scroll.
function TabButton({ active, onClick, icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition whitespace-nowrap text-sm ${
        active ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-surface-100'
      }`}
    >
      <span className="w-[18px] h-[18px] shrink-0">{icon}</span>
      {label}
      {badge}
    </button>
  );
}

function SectionHeader({ icon, color, title, subtitle }) {
  return (
    <div className="px-6 py-4 border-b border-surface-100 flex items-center gap-3">
      <span className={`w-10 h-10 rounded-xl bg-${color}-50 text-${color}-600 flex items-center justify-center shrink-0`}>
        {icon}
      </span>
      <div>
        <h2 className="text-lg font-display font-bold text-gray-800">{title}</h2>
        {subtitle && <p className="text-gray-500 text-sm">{subtitle}</p>}
      </div>
    </div>
  );
}

const ATTENDANCE_TONE = { present: 'green', absent: 'red', late: 'yellow', excused: 'blue' };
const PAYMENT_TONE = { completed: 'green', pending: 'yellow', failed: 'red', refunded: 'gray' };

function ParentChildProgress() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [child, setChild] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const [attemptDetail, setAttemptDetail] = useState(null);
  const [attemptDetailQuiz, setAttemptDetailQuiz] = useState(null);
  const [loadingAttemptDetail, setLoadingAttemptDetail] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [dash, enr, prog, att, subs, attnd, pay] = await Promise.all([
          userService.getDashboard(),
          parentService.getChildEnrollments(childId),
          parentService.getChildProgress(childId),
          parentService.getChildQuizAttempts(childId),
          parentService.getAssignmentSubmissions(),
          parentService.getChildAttendance(childId),
          parentService.getChildPayments(childId),
        ]);
        if (!mounted) return;
        const kids = dash.data.children || [];
        setChild(kids.find((c) => String(c.id) === String(childId)) || null);
        const enrData = enr.data;
        setEnrollments(Array.isArray(enrData) ? enrData : enrData.results || []);
        const progData = prog.data;
        setProgress(Array.isArray(progData) ? progData : progData.results || []);
        const attData = att.data;
        setAttempts(Array.isArray(attData) ? attData : attData.results || []);
        const subsData = subs.data;
        const subsList = Array.isArray(subsData) ? subsData : subsData.results || [];
        setAssignments(subsList.filter((s) => String(s.student?.id) === String(childId)));
        const attndData = attnd.data;
        setAttendance(Array.isArray(attndData) ? attndData : attndData.results || []);
        const payData = pay.data;
        setPayments(Array.isArray(payData) ? payData : payData.results || []);
      } catch (e) {
        addNotification(t('Failed to load child data'), 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  // Attempt detail includes the authorized quiz structure. Keeping this in a
  // single request avoids checking paid-course access against the parent when
  // it is the linked child who took the quiz.
  const openAttemptDetail = async (attempt) => {
    setAttemptDetail(attempt);
    setAttemptDetailQuiz(null);
    setLoadingAttemptDetail(true);
    try {
      const attemptRes = await quizService.getAttempt(attempt.id);
      const detail = attemptRes.data || attemptRes;
      setAttemptDetail(detail);
      setAttemptDetailQuiz(detail.quiz_details || null);
    } catch (error) {
      addNotification(t('Failed to load attempt details'), 'error');
    } finally {
      setLoadingAttemptDetail(false);
    }
  };

  const closeAttemptDetail = () => {
    setAttemptDetail(null);
    setAttemptDetailQuiz(null);
  };

  if (loading) return <FullPageSpinner />;

  const avgQuizScore = attempts.length
    ? Math.round(attempts.reduce((sum, a) => sum + Number(a.score || 0), 0) / attempts.length)
    : null;
  const attendanceRate = attendance.length
    ? Math.round((attendance.filter((a) => a.status === 'present').length / attendance.length) * 100)
    : null;
  const gradedAssignments = assignments.filter((a) => a.status === 'graded');

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={child ? t("{{name}}'s Progress", { name: child.name }) : t('Child Progress')} subtitle={child?.email} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-primary-50 rounded-2xl p-4">
              <p className="text-xs font-medium text-primary-700 opacity-80">{t('Active Courses')}</p>
              <p className="text-3xl font-display font-bold text-primary-700 mt-1 tabular-nums">{enrollments.filter((e) => e.status === 'active').length}</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-4">
              <p className="text-xs font-medium text-purple-700 opacity-80">{t('Avg. Quiz Score')}</p>
              <p className="text-3xl font-display font-bold text-purple-700 mt-1 tabular-nums">{avgQuizScore != null ? `${avgQuizScore}%` : '—'}</p>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-4">
              <p className="text-xs font-medium text-emerald-700 opacity-80">{t('Attendance Rate')}</p>
              <p className="text-3xl font-display font-bold text-emerald-700 mt-1 tabular-nums">{attendanceRate != null ? `${attendanceRate}%` : '—'}</p>
            </div>
            <div className="bg-amber-50 rounded-2xl p-4">
              <p className="text-xs font-medium text-amber-700 opacity-80">{t('Assignments Graded')}</p>
              <p className="text-3xl font-display font-bold text-amber-700 mt-1 tabular-nums">{gradedAssignments.length}/{assignments.length}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-card mb-4">
            <div className="flex overflow-x-auto gap-1 p-2">
              <TabButton
                active={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
                label={t('Overview')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>}
              />
              <TabButton
                active={activeTab === 'quizzes'}
                onClick={() => setActiveTab('quizzes')}
                label={t('Quizzes')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>}
              />
              <TabButton
                active={activeTab === 'assignments'}
                onClick={() => setActiveTab('assignments')}
                label={t('Assignments')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h3m0 0l-3-3m3 3l-3 3M13 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-2" /></svg>}
              />
              <TabButton
                active={activeTab === 'attendance'}
                onClick={() => setActiveTab('attendance')}
                label={t('Attendance')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>}
              />
              <TabButton
                active={activeTab === 'payments'}
                onClick={() => setActiveTab('payments')}
                label={t('Payments')}
                icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-9m12 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2z" /></svg>}
              />
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <SectionHeader
                  color="primary"
                  title={t('Enrollments')}
                  subtitle={t('{{count}} courses', { count: enrollments.length })}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>}
                />
                <div className="p-6">
                  {enrollments.length === 0 ? (
                    <EmptyState title={t('No enrollments')} description={t("This child isn't enrolled in any module yet.")} />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {enrollments.map((e) => (
                        <div key={e.id} className="border border-surface-200 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-900">{e.module_name}</p>
                              <p className="text-xs text-gray-500">{e.grade_name} · {e.subscription_name}</p>
                            </div>
                            <Badge status={e.status} />
                          </div>
                          {e.status === 'active' && (
                            <Button size="sm" variant="secondary" className="mt-3" onClick={() => navigate(`/modules/${e.module}`)}>
                              {t('View Module')}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <SectionHeader
                  color="primary"
                  title={t('Lesson Progress')}
                  subtitle={t('{{count}} lessons', { count: progress.length })}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>}
                />
                {progress.length === 0 ? (
                  <div className="p-6"><EmptyState title={t('No lesson progress recorded yet')} /></div>
                ) : (
                  <div className="divide-y divide-surface-100">
                    {progress.map((p) => (
                      <div key={p.id} className="p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{p.lesson_title}</p>
                          <p className="text-xs text-gray-500">{p.module_name}</p>
                          <div className="mt-2"><ProgressBar value={p.completion_percentage} /></div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-gray-700">{p.completion_percentage}%</p>
                          {p.is_completed && <Badge tone="green">{t('Done')}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'quizzes' && (
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <SectionHeader
                color="purple"
                title={t('Quiz Results')}
                subtitle={t('{{count}} attempts', { count: attempts.length })}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>}
              />
              {attempts.length === 0 ? (
                <div className="p-6"><EmptyState title={t('No quiz attempts yet')} /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-50 text-gray-500 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">{t('Quiz')}</th>
                        <th className="text-left px-4 py-3">{t('Attempt')}</th>
                        <th className="text-left px-4 py-3">{t('Score')}</th>
                        <th className="text-left px-4 py-3">{t('Result')}</th>
                        <th className="text-left px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-100">
                      {attempts.map((a) => (
                        <tr key={a.id} className="hover:bg-surface-50 transition">
                          <td className="px-4 py-3 font-medium text-gray-900">{a.quiz_title}</td>
                          <td className="px-4 py-3 text-gray-600">#{a.attempt_number}</td>
                          <td className="px-4 py-3 text-gray-600 tabular-nums">{Number(a.score).toFixed(1)}%</td>
                          <td className="px-4 py-3"><Badge tone={a.passed ? 'green' : 'red'}>{a.passed ? t('Passed') : t('Failed')}</Badge></td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => openAttemptDetail(a)} className="text-primary-600 hover:text-primary-700 font-medium text-xs">
                              {t('View Details')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'assignments' && (
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <SectionHeader
                color="amber"
                title={t('Assignments')}
                subtitle={t('{{count}} submissions', { count: assignments.length })}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h3m0 0l-3-3m3 3l-3 3M13 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-2" /></svg>}
              />
              {assignments.length === 0 ? (
                <div className="p-6"><EmptyState title={t('No assignment submissions yet')} /></div>
              ) : (
                <div className="divide-y divide-surface-100">
                  {assignments.map((s) => (
                    <div key={s.id} className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{s.assignment_title}</p>
                        <p className="text-xs text-gray-500">{t('Submitted')}: {new Date(s.submitted_at).toLocaleDateString()}</p>
                        {s.feedback && <p className="text-xs text-gray-500 mt-1">{t('Feedback')}: {s.feedback}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        {s.status === 'graded' ? (
                          <Badge tone="green">{t('{{grade}}/{{max}}', { grade: s.grade, max: s.max_points })}</Badge>
                        ) : (
                          <Badge tone="blue">{t('Submitted')}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'attendance' && (
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <SectionHeader
                color="emerald"
                title={t('Attendance')}
                subtitle={t('{{count}} sessions', { count: attendance.length })}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>}
              />
              {attendance.length === 0 ? (
                <div className="p-6"><EmptyState title={t('No attendance records yet')} /></div>
              ) : (
                <div className="divide-y divide-surface-100">
                  {attendance.map((a) => (
                    <div key={a.id} className="p-4 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{a.event_title}</p>
                        <p className="text-xs text-gray-500">{a.module_name} · {new Date(a.event_start).toLocaleDateString()}</p>
                      </div>
                      <Badge tone={ATTENDANCE_TONE[a.status] || 'gray'}>{t(a.status)}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <SectionHeader
                color="primary"
                title={t('Payments')}
                subtitle={t('{{count}} transactions', { count: payments.length })}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-9m12 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2z" /></svg>}
              />
              {payments.length === 0 ? (
                <div className="p-6"><EmptyState title={t('No payment records yet')} /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-50 text-gray-500 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">{t('Course')}</th>
                        <th className="text-left px-4 py-3">{t('Amount')}</th>
                        <th className="text-left px-4 py-3">{t('Date')}</th>
                        <th className="text-left px-4 py-3">{t('Status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-100">
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td className="px-4 py-3 font-medium text-gray-900">{p.enrollment_details?.module_name || '—'}</td>
                          <td className="px-4 py-3 text-gray-600 tabular-nums">{Number(p.amount).toFixed(2)} {p.currency}</td>
                          <td className="px-4 py-3 text-gray-600">{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-3"><Badge tone={PAYMENT_TONE[p.status] || 'gray'}>{t(p.status)}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Quiz Attempt Detail Modal */}
      <Modal
        open={!!attemptDetail}
        onClose={closeAttemptDetail}
        title={attemptDetail?.quiz_title || t('Attempt Details')}
        size="lg"
        zIndexClass="z-[60]"
      >
        {attemptDetail && (
          <>
            <p className="text-sm text-gray-500 -mt-2 mb-4">
              {t('Score')}: {attemptDetail.score}% • {attemptDetail.points_earned}/{attemptDetail.total_points} {t('points')}
            </p>
            {loadingAttemptDetail ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
              </div>
            ) : attemptDetailQuiz ? (
              <div className="space-y-5">
                {!attemptDetailQuiz.show_correct_answers && (
                  <p className="text-sm text-center text-gray-500 bg-gray-50 rounded-lg p-3">
                    {t('Correct answers are hidden by the teacher. The student\'s submitted answers are shown below.')}
                  </p>
                )}
                {(attemptDetailQuiz.questions || []).map((question, index) => {
                  const response = (attemptDetail.responses || []).find((r) => r.question === question.id);
                  const selectedIds = response?.selected_answers || [];
                  const correctnessVisible = response?.is_correct !== null && response?.is_correct !== undefined;
                  return (
                    <div key={question.id} className="border border-surface-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-medium text-gray-800 flex-1">{index + 1}. {question.text}</p>
                        {response && correctnessVisible && (
                          <span className={`text-xs font-medium px-2 py-1 rounded-full shrink-0 ${response.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {response.is_correct ? t('Correct') : t('Incorrect')} · {response.points_earned || 0}/{question.points} {t('pts')}
                          </span>
                        )}
                      </div>
                      <ul className="mt-3 space-y-1.5">
                        {(question.answers || []).map((answer) => {
                          const wasSelected = selectedIds.includes(answer.id);
                          const selectedTone = correctnessVisible
                            ? (response?.is_correct ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50')
                            : 'border-primary-300 bg-primary-50';
                          const selectedTextTone = correctnessVisible
                            ? (response?.is_correct ? 'text-green-800 font-medium' : 'text-red-800')
                            : 'text-primary-800 font-medium';
                          return (
                            <li
                              key={answer.id}
                              className={`text-sm flex items-center gap-2 px-3 py-2 rounded-lg border ${
                                wasSelected ? selectedTone : 'border-gray-100'
                              }`}
                            >
                              {wasSelected && correctnessVisible ? (
                                response?.is_correct ? (
                                  <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                ) : (
                                  <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                )
                              ) : wasSelected ? (
                                <span className="w-4 h-4 rounded-full bg-primary-500 ring-4 ring-primary-100 shrink-0" />
                              ) : (
                                <span className="w-4 h-4 shrink-0" />
                              )}
                              <span className={wasSelected ? selectedTextTone : 'text-gray-600'}>
                                {answer.text}
                              </span>
                              {wasSelected && <span className="text-xs text-gray-500 ml-auto">{t('Student\'s answer')}</span>}
                            </li>
                          );
                        })}
                      </ul>
                      {question.explanation && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600"><span className="font-semibold">{t('Note:')}</span> {question.explanation}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">{t('Failed to load attempt details')}</p>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}

export default ParentChildProgress;
